import { Box, Link, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CheckIcon from '@mui/icons-material/Check'
import PageHero from '../shared/PageHero'
import Section from '../shared/Section'
import TvButton from '../shared/TvButton'
import Tag from '../shared/Tag'
import CtaBanner from '../shared/CtaBanner'
import { COLORS } from '../../constants'
import { services } from '../../utils/siteContent'
import usePageMeta from '../../utils/usePageMeta'

function Services() {
    usePageMeta({
        title: 'Services: Websites, Custom Software, Automation & AWS Consulting',
        description: 'Small business websites, custom software development, business automation, AWS and architecture consulting, technical rescue for struggling applications, and developer mentorship.',
        path: '/services',
    })

    return (
        <>
            <PageHero
                eyebrow="Services"
                title="Practical software for real business problems"
                intro="Whether you need something new built, something manual automated, or something existing fixed, here’s how I can help and what you can expect to get out of it."
                actions={<TvButton text="Start a Project" to="/contact" icon={<ArrowForwardIcon />} />}
            />

            {/* Jump links */}
            <Box component="nav" aria-label="Services" sx={{ backgroundColor: COLORS.light, borderBottom: `1px solid ${COLORS.border}`, overflowX: 'auto' }}>
                <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 2.5, sm: 4 }, py: 2, display: 'flex', gap: 1, flexWrap: { xs: 'nowrap', md: 'wrap' } }}>
                    {services.map((s) => (
                        <Link
                            key={s.id}
                            component={RouterLink}
                            to={s.id === 'mentorship' ? '/mentorship' : { hash: s.id }}
                            underline="none"
                            sx={{ whiteSpace: 'nowrap', px: 1.75, py: 0.75, borderRadius: 999, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.white, color: COLORS.black, fontSize: '0.92rem', fontWeight: 500, '&:hover': { borderColor: COLORS.accent } }}
                        >
                            {s.shortTitle}
                        </Link>
                    ))}
                </Box>
            </Box>

            {services.map((s, i) => (
                <Section key={s.id} id={s.id} tone={i % 2 === 0 ? 'white' : 'light'}>
                    <Box sx={{ display: 'grid', gap: { xs: 4, md: 8 }, gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' } }}>
                        <Box>
                            <Box sx={{ width: 52, height: 52, borderRadius: 2, display: 'grid', placeItems: 'center', backgroundColor: COLORS.black, color: COLORS.accent, mb: 3 }}>
                                {s.icon}
                            </Box>
                            <Typography variant="h2">{s.title}</Typography>
                            <Typography sx={{ mt: 2, fontSize: { xs: '1.1rem', md: '1.2rem' }, fontWeight: 500 }}>{s.summary}</Typography>
                            <Typography sx={{ mt: 2, color: 'text.secondary' }}>{s.description}</Typography>
                            <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                <TvButton text="Talk about this" to={`/contact?topic=${encodeURIComponent(s.contactTopic)}`} icon={<ArrowForwardIcon />} />
                                {s.id === 'mentorship' && <TvButton text="Mentorship details" to="/mentorship" variant="outline" />}
                            </Box>
                            {s.example && (
                                <Link component={RouterLink} to={s.example.to} sx={{ mt: 2.5, display: 'inline-block', color: COLORS.accentOnLight, fontWeight: 600 }}>
                                    {s.example.label} <ArrowForwardIcon fontSize="small" sx={{ verticalAlign: 'text-bottom' }} />
                                </Link>
                            )}
                        </Box>
                        <Box>
                            <Typography variant="h5" component="h3" sx={{ mb: 2 }}>What you get</Typography>
                            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 1.5 }}>
                                {s.outcomes.map((o) => (
                                    <Box component="li" key={o} sx={{ display: 'flex', gap: 1.5 }}>
                                        <CheckIcon sx={{ color: COLORS.accentOnLight, mt: 0.4, flexShrink: 0 }} fontSize="small" />
                                        <Typography>{o}</Typography>
                                    </Box>
                                ))}
                            </Box>
                            <Typography variant="h5" component="h3" sx={{ mt: 4, mb: 1.5 }}>Typical work</Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {s.examples.map((e) => <Tag key={e} text={e} />)}
                            </Box>
                            {s.pricing && (
                                <Box sx={{ mt: 4, p: 3, borderRadius: 3, backgroundColor: COLORS.white, border: `1px solid ${COLORS.border}` }}>
                                    <Typography variant="overline" component="p" sx={{ color: COLORS.accentOnLight }}>Pricing</Typography>
                                    <Typography component="p" sx={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                                        Starting at {s.pricing.startingAt}
                                    </Typography>
                                    <Typography sx={{ mt: 1 }}>{s.pricing.scope}</Typography>
                                    <Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600 }}>Includes</Typography>
                                    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, mt: 0.75, display: 'grid', gap: 0.75, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
                                        {s.pricing.includes.map((item) => (
                                            <Box component="li" key={item} sx={{ display: 'flex', gap: 1 }}>
                                                <CheckIcon sx={{ color: COLORS.accentOnLight, mt: 0.3, flexShrink: 0 }} fontSize="small" />
                                                <Typography variant="body2">{item}</Typography>
                                            </Box>
                                        ))}
                                    </Box>
                                    <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>{s.pricing.quotedSeparately}</Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Section>
            ))}

            <CtaBanner title="Not sure which of these fits?" body="That’s normal. Describe the problem in plain language and I’ll tell you how I’d approach it, or point you somewhere better if I’m not the right fit." />
        </>
    )
}

export default Services
